"use client";

import { useEffect } from "react";
import { PlatformDataEvents } from "../PlatformDataEvents";
import initialState from "@/lib/StaticData/searchFormData";
import { useFilterState } from "../../Providers/FilterStateProvider/FilterStateProvider";
import {
  getCategoryMapping,
  processCategoryChanges,
  isDeselection,
} from "@/lib/Helpers/filterTrackingUtils";
import { usePlatformDataTracking } from "@/lib/PlatformData/platformDataStore";
import { ServerConfig } from "@/lib/Helpers/getServerConfig";

const flexiType2EventAction = (value: string | number | boolean) => {
  return value === "freeRebooking" ? "free-rebooking" : "flexi";
};

type FilterMappings = {
  directFlight: { action: string };
  flexibleOffers: { action: string };
  mealType: { action: string };
  minRecommendation: { action: string };
  oceanView: { action: string };
  price: { action: string };
  railAndFly: { action: string };
  roomType: { action: string };
  specialDeals: { action: string };
  stars: { action: string };
  tourOperator: { action: string };
  transfer: { action: string };
  rentalCar: { action: string };
};

const filterMappings: FilterMappings = {
  directFlight: { action: "flight-flug_und_transfer-direktflug" },
  flexibleOffers: { action: flexiType2EventAction(false) },
  mealType: { action: "schlafen_und_essen-verplegung" },
  minRecommendation: { action: "weiterempfehlungstrate" },
  oceanView: { action: "sleep_and_food-zimmer_mit_meerblick" },
  price: { action: "gesamtpreis" },
  railAndFly: { action: "flug_und_transfer-zug_zum_flughafen" },
  roomType: { action: "schlafen_und_essen-zimmertyp" },
  specialDeals: { action: "special_deals" },
  stars: { action: "hotelkategorie" },
  tourOperator: { action: "reiseveranstalter" },
  transfer: { action: "flug_und_transfer" },
  rentalCar: { action: "flug_und_transfer" },
};

// Flight time filter keys that should be tracked together as a single event
const FLIGHT_TIME_FILTER_KEYS = [
  "departureFlightTimeFrom",
  "departureFlightTimeUntil",
  "returnFlightTimeFrom",
  "returnFlightTimeUntil",
] as const;

// Helper to format minutes to HH.MM format
const formatMinutesToTime = (minutes: number): string => {
  const hours = Math.floor(minutes / 60)
    .toString()
    .padStart(2, "0");
  const mins = (minutes % 60).toString().padStart(2, "0");
  return `${hours}.${mins}`;
};

const getFilterLabel = (
  key: string,
  value: string | undefined,
  deselected: boolean
): string => {
  switch (key) {
    case "specialDeals":
      return value === "true" ? "on" : "off";
    case "tourOperator":
      return !value || value === "false" ? "all_tour_operators" : value;
    case "transfer":
    case "rentalCar":
      const transferType = value === "1" ? "transfer" : "rentalCar";
      return value === "1"
        ? initialState.transfer.byId[transferType]?.label?.split(" ").join("_")
        : "beliebig";
    case "price":
      return deselected ? "deselected" : value || "";
    case "stars":
      return deselected ? "deselected" : value || "";
    case "minRecommendation":
      return deselected ? "deselected" : value || "";
    case "mealType":
      if (deselected) {
        return "beliebig";
      } else {
        const mealTypeId = Number(value);
        if (mealTypeId in initialState.mealType.byId) {
          return initialState.mealType.byId[
            mealTypeId as keyof typeof initialState.mealType.byId
          ]?.label;
        }
        return value || "";
      }
    case "roomType":
      if (deselected) {
        return "beliebig";
      } else {
        const roomTypeId = Number(value);
        if (roomTypeId in initialState.roomType.byId) {
          return initialState.roomType.byId[roomTypeId]?.label;
        }
        return value || "";
      }
    default:
      return value || "deselected";
  }
};

export const usePlatformDataFilterTracking = (serverConfig: ServerConfig) => {
  const { pushEvent } = usePlatformDataTracking();
  const { viewMode } = serverConfig;
  const { subscribeToFilterChanges } = useFilterState();

  useEffect(() => {
    if (!viewMode) return;

    const unsubscribe = subscribeToFilterChanges(
      (changedParams, removedParams, currentParams, prevParams, options) => {
        // If this is a remove all action, just send that event and skip individual tracking
        if (options?.isRemoveAllAction) {
          pushEvent({
            event: PlatformDataEvents.USER_INTERACTION,
            user: {
              interaction: {
                action: "alle_filter_entfernen",
                category: "filters",
                label: "click",
                n: 0,
              },
            },
          });
          return; // Skip individual filter tracking
        }

        // Handle category changes
        if (
          changedParams.some(([key]) => key === "categories") ||
          removedParams.some(([key]) => key === "categories")
        ) {
          processCategoryChanges(
            currentParams,
            prevParams,
            (categoryId, isDeselected) => {
              const action = getCategoryMapping(Number(categoryId));
              if (action) {
                pushEvent({
                  event: PlatformDataEvents.USER_INTERACTION,
                  user: {
                    interaction: {
                      action,
                      category: "filters",
                      label: isDeselected ? "deselected" : "selected",
                      n: 0,
                    },
                  },
                });
              }
            }
          );
        }

        // Track flight time filter changes (departure and return)
        const allChangedKeys = [
          ...changedParams.map(([key]) => key),
          ...removedParams.map(([key]) => key),
        ];

        // Check if departure flight time changed
        if (
          allChangedKeys.some((key) =>
            ["departureFlightTimeFrom", "departureFlightTimeUntil"].includes(key)
          )
        ) {
          const from = Number(currentParams.departureFlightTimeFrom) || 0;
          const until = Number(currentParams.departureFlightTimeUntil) || 1440;
          const label = `${formatMinutesToTime(from)}-${formatMinutesToTime(until)}`;

          pushEvent({
            event: PlatformDataEvents.USER_INTERACTION,
            user: {
              interaction: {
                action: "flug_und_transfer-abflugszeit_hinreise",
                category: "filters",
                label,
                n: 0,
              },
            },
          });
        }

        // Check if return flight time changed
        if (
          allChangedKeys.some((key) =>
            ["returnFlightTimeFrom", "returnFlightTimeUntil"].includes(key)
          )
        ) {
          const from = Number(currentParams.returnFlightTimeFrom) || 0;
          const until = Number(currentParams.returnFlightTimeUntil) || 1440;
          const label = `${formatMinutesToTime(from)}-${formatMinutesToTime(until)}`;

          pushEvent({
            event: PlatformDataEvents.USER_INTERACTION,
            user: {
              interaction: {
                action: "flug_und_transfer-abflugszeit_ruckreise",
                category: "filters",
                label,
                n: 0,
              },
            },
          });
        }

        // Track regular filter changes (exclude flight time filters)
        [...changedParams, ...removedParams]
          .filter(
            ([key]) =>
              key in filterMappings &&
              !FLIGHT_TIME_FILTER_KEYS.includes(
                key as (typeof FLIGHT_TIME_FILTER_KEYS)[number]
              )
          )
          .forEach(([key]) => {
            const value = currentParams[key]; // will be undefined for removed params
            const prevValue = prevParams[key];
            const deselected = isDeselection(key, value, prevValue);

            const label = getFilterLabel(key, value, deselected);

            if (label && filterMappings[key as keyof FilterMappings]) {
              pushEvent({
                event: PlatformDataEvents.USER_INTERACTION,
                user: {
                  interaction: {
                    action: filterMappings[key as keyof FilterMappings].action,
                    category: "filters",
                    label,
                    n: 0,
                  },
                },
              });
            }
          });
      }
    );

    return unsubscribe;
  }, [viewMode, pushEvent, subscribeToFilterChanges]);
};
